import type { FC } from 'react';
import type {
    FieldValues,
    FieldArray,
    FieldArrayMethodProps,
    FieldArrayPath,
    FieldError,
    DeepMap,
} from 'react-hook-form';

import { FormInput } from '@catalyze/ui';

import { RemoveButton, AddButton, FormLine } from './styled';

const MAX_FIELDS_LENGTH = 3; // maximum 3 phones/emails
const MIN_FIELDS_LENGTH = 1; // minimum 1 phone/email

// Inspired by https://react-hook-form.com/ts#FieldValues
export type FieldErrors<TFieldValues extends FieldValues = FieldValues> =
    DeepMap<TFieldValues, FieldError>;

interface IInputFieldsArray<
    TFieldValues extends FieldValues = FieldValues,
    TFieldArrayName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>
> {
    name: string; // name passed to useFieldArray hook
    addInputLabel: string;
    label: string;
    inputFields: Array<{ value: string }>;
    onRemoveField: (index: number) => void;
    onAddField: (
        value:
            | Partial<FieldArray<TFieldValues, TFieldArrayName>>
            | Array<Partial<FieldArray<TFieldValues, TFieldArrayName>>>,
        options?: FieldArrayMethodProps
    ) => void;
}

// Components render array of input fields
export const InputFieldsArray: FC<IInputFieldsArray> = ({
    name,
    addInputLabel,
    label,
    inputFields,
    onRemoveField,
    onAddField,
}) => {
    const inputFieldsLength = inputFields.length;

    const handleAddNewInput = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault(); // prevent for validation form when adding new input

        onAddField({
            value: '',
        });
    };

    return (
        <>
            {inputFields.map((item, index) => {
                return (
                    // eslint-disable-next-line react/no-array-index-key
                    <FormLine key={`${item.value}-${index}`}>
                        <FormInput
                            id={`${name}.${index}.value`}
                            label={label}
                        />
                        {inputFieldsLength > MIN_FIELDS_LENGTH && (
                            <RemoveButton
                                type="button"
                                onClick={() => onRemoveField(index)}
                            >
                                Remove
                            </RemoveButton>
                        )}
                    </FormLine>
                );
            })}
            {inputFieldsLength < MAX_FIELDS_LENGTH && (
                <AddButton onClick={handleAddNewInput}>
                    {addInputLabel}
                </AddButton>
            )}
        </>
    );
};
